import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from 'pdfmake/build/vfs_fonts';
import useDate from "../day";
import figureFormatter from "@/services/utils/figureFormatter";
import { loadImageAsBase64 } from "@/services/utils/global";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
  SulaimanLipi: {
    normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
    bold: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
    italics: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
    bolditalics: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff'
  }
};

const printTdsCertificate = () => {
  const { formatDate } = useDate();
  const { commaFormat, currencyToWord } = figureFormatter();

  const exportToPdfTdsCertificate = async (companyInfo, certificate, bills, challans, payee, issuer) => {
    const imagePath = await loadImageAsBase64('/app-assets/images/gov_seal.png');
    const doc = {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [40, 55, 40, 30],
      defaultStyle: {
        font: "SulaimanLipi",
        fontSize: 9
      },
      content: getContent(companyInfo, certificate, bills, challans, payee, issuer, imagePath),
      info: {
        title: 'Certificate of Deduction of Tax'
      },
      styles: styles
    };
    pdfMake.createPdf(doc).open();
  }

  const getContent = (companyInfo, certificate, bills, challans, payee, issuer, imagePath) => {
    return [
      getHeaderBlock(imagePath),
      { text: 'Certificate of Deduction of Tax', style: 'h2', margin: [0, 4, 0, 0] },
      { text: '[(section 58 of the Income Tax Ordinance, 1984 (XXXVI of 1984)]', style: 'small', margin: [0, 0, 0, 8] },
      getPayeeTable(certificate, payee),
      { text: '06. Particulars of the making of payment and the deduction of tax (add lines if necessary)', style: 'sectionHead', margin: [0, 10, 0, 4] },
      getPaymentTable(bills),
      { text: '07. Payment of deducted tax to the credit of the Government (add lines if necessary)', style: 'sectionHead', margin: [0, 10, 0, 4] },
      getChallanTable(bills, challans),
      { text: '*if payment is made in any other mode specified by the Board, provide information relevant to that mode.', style: 'small', alignment: 'left', margin: [0, 6, 0, 0] },
      { text: 'Certified that the information given above is correct and complete.', style: 'info', margin: [0, 10, 0, 0] },
      getSignatureBlock(certificate, issuer)
    ];
  }

  const getHeaderBlock = (imagePath) => {
    const seal = imagePath ? { image: imagePath, width: 45, height: 45 } : { width: 45, text: '' };
    return {
      margin: [0, 0, 0, 2],
      columns: [
        seal,
        {
          width: '*',
          stack: [
            { text: 'Government of the People\'s Republic of Bangladesh', style: 'h1' },
            { text: 'National Board of Revenue', style: 'h1', margin: [0, 3, 0, 0] }
          ]
        },
        {
          width: 55,
          margin: [0, 12, 0, 0],
          table: { widths: ['*'], body: [[{ text: 'TDS', alignment: 'center', fontSize: 9 }]] },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => '#000',
            vLineColor: () => '#000'
          }
        }
      ]
    };
  }

  const getPayeeTable = (certificate, payee) => {
    return {
      table: {
        widths: [20, 200, 90, 90, '*'],
        body: [
          [
            { text: '1', style: 'td' },
            { text: 'Name of the payee:', style: 'tdLeft' },
            { text: getPayeeValue(payee, 'full_name'), style: 'tdLeft', colSpan: 2 }, {},
            { text: 'No.: ' + getValue(certificate.certificate_number), style: 'tdLeft' }
          ],
          [
            { text: '2', style: 'td' },
            { text: 'Address of the payee:', style: 'tdLeft' },
            { text: getPayeeValue(payee, 'full_address'), style: 'tdLeft', colSpan: 2 }, {},
            { text: 'Date: ' + getRowDate(certificate.date), style: 'tdLeft' }
          ],
          [
            { text: '3', style: 'td' },
            { text: 'Does the payee have a Twelve-digit TIN?', style: 'tdLeft' },
            { text: getTinAnswer(payee, true), style: 'td' },
            { text: getTinAnswer(payee, false), style: 'td' },
            { text: '', style: 'td' }
          ],
          [
            { text: '4', style: 'td' },
            { text: 'Twelve-digit TIN (if answer of 03 is Yes)', style: 'tdLeft' },
            { text: getPayeeValue(payee, 'tin_no'), style: 'tdLeft', colSpan: 3 }, {}, {}
          ],
          [
            { text: '5', style: 'td' },
            { text: 'Period for which payment is made From (date) to (date)', style: 'tdLeft' },
            { text: getPeriod(certificate), style: 'tdLeft', colSpan: 3 }, {}, {}
          ]
        ]
      },
      layout: tableLayout()
    };
  }

  const getPaymentTable = (bills) => {
    return {
      table: {
        headerRows: 2,
        widths: [22, 70, '*', 60, 70, 70, 60],
        body: [
          [
            { text: 'Sl', style: 'th' },
            { text: 'Date of payment', style: 'th' },
            { text: 'Description of payment', style: 'th' },
            { text: 'Section', style: 'th' },
            { text: 'Amount of payment (Taka)', style: 'th' },
            { text: 'Amount of tax deducted (Taka)', style: 'th' },
            { text: 'Remarks', style: 'th' }
          ],
          getPaymentNumberRow(),
          ...getPaymentRows(bills),
          getPaymentTotalRow(bills)
        ]
      },
      layout: tableLayout()
    };
  }

  const getPaymentNumberRow = () => {
    const nums = ['(1)', '(2)', '(3)', '(4)', '(5)', '(6)', '(7)'];
    return nums.map(function (n) {
      return { text: n, style: 'th' };
    });
  }

  const getPaymentRows = (bills) => {
    if (!bills || !bills.length) return [getBlankRow(7)];
    return bills.map((item, index) => mapPaymentRow(item, index));
  }

  const mapPaymentRow = (item, index) => {
    return [
      { text: index + 1, style: 'td' },
      { text: getRowDate(item.payment_date), style: 'td' },
      { text: getDescription(item), style: 'tdLeft' },
      { text: getValue(item.tds_code), style: 'td' },
      { text: money(item.base_amount), style: 'tdRight' },
      { text: money(item.tds_amount), style: 'tdRight' },
      { text: '', style: 'td' }
    ];
  }

  const getPaymentTotalRow = (bills) => {
    return [
      { text: 'Total', style: 'thRight', colSpan: 4 }, {}, {}, {},
      { text: money(sumBy(bills, 'base_amount')), style: 'thRight' },
      { text: money(sumBy(bills, 'tds_amount')), style: 'thRight' },
      { text: '', style: 'td' }
    ];
  }

  const getChallanTable = (bills, challans) => {
    const totalTds = sumBy(bills, 'tds_amount');
    return {
      table: {
        headerRows: 2,
        widths: [22, 75, 65, '*', 70, 75, 55],
        body: [
          [
            { text: 'Sl', style: 'th' },
            { text: 'Challan* Number', style: 'th' },
            { text: 'Challan date', style: 'th' },
            { text: 'Bank Name', style: 'th' },
            { text: 'Total amount in the challan (Taka)', style: 'th' },
            { text: 'Amount relating to this certificate (Taka)', style: 'th' },
            { text: 'Remarks', style: 'th' }
          ],
          getChallanNumberRow(),
          ...getChallanRows(challans, totalTds),
          getChallanTotalRow(challans, totalTds),
          [
            { text: 'In words: ' + inWords(totalTds), style: 'tdLeft', colSpan: 7 }, {}, {}, {}, {}, {}, {}
          ]
        ]
      },
      layout: tableLayout()
    };
  }

  const getChallanNumberRow = () => {
    const nums = ['(1)', '(2)', '(3)', '(4)', '(5)', '(6)', '(7)'];
    return nums.map(function (n) {
      return { text: n, style: 'th' };
    });
  }

  const getChallanRows = (challans, totalTds) => {
    if (!challans || !challans.length) return [getBlankRow(7)];
    return challans.map((item, index) => mapChallanRow(item, index, totalTds));
  }

  const mapChallanRow = (item, index, totalTds) => {
    return [
      { text: index + 1, style: 'td' },
      { text: getValue(item.challan_no), style: 'td' },
      { text: getRowDate(item.challan_date), style: 'td' },
      { text: getValue(item.bank_name), style: 'tdLeft' },
      { text: money(item.amount), style: 'tdRight' },
      { text: getRelatedAmount(index, totalTds), style: 'tdRight' },
      { text: '', style: 'td' }
    ];
  }

  const getChallanTotalRow = (challans, totalTds) => {
    return [
      { text: 'Total', style: 'thRight', colSpan: 4 }, {}, {}, {},
      { text: money(sumBy(challans, 'amount')), style: 'thRight' },
      { text: money(totalTds), style: 'thRight' },
      { text: '', style: 'td' }
    ];
  }

  const getSignatureBlock = (certificate, issuer) => {
    return {
      margin: [0, 20, 0, 0],
      table: {
        widths: ['*', 'auto'],
        body: [
          [
            { text: 'Name of the deducting officer: ' + getIssuerValue(issuer, 'full_name'), style: 'info', border: noBorder() },
            { text: 'Signature and seal:', style: 'info', alignment: 'right', border: noBorder() }
          ],
          [
            { text: 'Designation: ' + getIssuerValue(issuer, 'designation'), style: 'info', border: noBorder() },
            { text: '', border: noBorder() }
          ],
          [
            { text: 'TCAN: ' + getIssuerValue(issuer, 'tin_no'), style: 'info', border: noBorder() },
            { text: '', border: noBorder() }
          ],
          [
            { text: 'Phone & E-mail:', style: 'info', border: noBorder() },
            { text: 'Date: ' + getRowDate(certificate.date), style: 'info', alignment: 'right', border: noBorder() }
          ]
        ]
      }
    };
  }

  const noBorder = () => {
    return [false, false, false, false];
  }

  const getRelatedAmount = (index, totalTds) => {
    if (index !== 0) return '';
    return money(totalTds);
  }

  const getPayeeValue = (payee, key) => {
    if (!payee) return '';
    return getValue(payee[key]);
  }

  const getIssuerValue = (issuer, key) => {
    if (!issuer) return '';
    return getValue(issuer[key]);
  }

  const getTinAnswer = (payee, isYesColumn) => {
    if (isYesColumn) return hasTin(payee) ? 'Yes' : '';
    return hasTin(payee) ? '' : 'No';
  }

  const hasTin = (payee) => {
    if (!payee || !payee.tin_no) return false;
    return String(payee.tin_no).replace(/\D/g, '').length === 12;
  }

  const getDescription = (item) => {
    const parts = [];
    if (item.bill_number) parts.push(item.bill_number);
    if (item.account_head_name) parts.push(item.account_head_name);
    if (item.tds_section_ref) parts.push(item.tds_section_ref);
    return parts.join(' - ');
  }

  const getPeriod = (certificate) => {
    if (!certificate.from_date && !certificate.to_date) return '';
    return getRowDate(certificate.from_date) + ' to ' + getRowDate(certificate.to_date);
  }

  const getBlankRow = (count) => {
    return Array.from({ length: count }, () => ({ text: ' ', style: 'td' }));
  }

  const getRowDate = (date) => {
    if (!date) return '';
    return formatDate(new Date(date)).split(' ')[0];
  }

  const getValue = (value) => {
    if (!value) return '';
    return value;
  }

  const getNumber = (value) => {
    if (!value) return 0;
    return parseFloat(value);
  }

  const sumBy = (list, key) => {
    if (!list || !list.length) return 0;
    return list.reduce((acc, item) => acc + getNumber(item[key]), 0);
  }

  const money = (value) => {
    if (!value) return 0;
    return commaFormat(value);
  }

  const inWords = (value) => {
    if (!value) return '';
    return currencyToWord(value);
  }

  const tableLayout = () => {
    return {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#000',
      vLineColor: () => '#000',
      paddingLeft: () => 2,
      paddingRight: () => 2,
      paddingTop: () => 2,
      paddingBottom: () => 2
    };
  }

  const styles = {
    h1: { fontSize: 12, alignment: 'center' },
    h2: { fontSize: 11, alignment: 'center' },
    small: { fontSize: 9, alignment: 'center' },
    info: { fontSize: 9, alignment: 'left' },
    justify: { fontSize: 9, alignment: 'justify' },
    sectionHead: { fontSize: 9, alignment: 'left' },
    th: { fontSize: 8, alignment: 'center', lineHeight: 1.1 },
    thRight: { fontSize: 8, alignment: 'right' },
    td: { fontSize: 8, alignment: 'center' },
    tdRight: { fontSize: 8, alignment: 'right' },
    tdLeft: { fontSize: 8, alignment: 'left' }
  };

  return {
    exportToPdfTdsCertificate
  }
}

export default printTdsCertificate;
